import { Card, Box, Typography, Collapse, Button } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useState, useRef } from "react";
import { TaskerConfig, TaskerRewardOption } from "@t3/shared";
import { PointsBadge } from "@/components/tasker/PointsBadge";
import { FirebaseImage } from "@/components/common";
import MarkdownRenderer from "@/components/MarkdownRenderer";
import {
  GradientRewardBox,
  RewardText,
} from "@/components/tasker/GradientRewardBox";
import root from "react-shadow";
import { useMediaQuery, useTheme } from "@mui/material";
interface ProgramMediaCardProps {
  config: TaskerConfig;
}

interface ProgramRewardOptionProps {
  option: TaskerRewardOption;
  programColor: string;
}

const ProgramRewardOption = ({
  option,
  programColor,
}: ProgramRewardOptionProps) => {
  return (
    <Box data-cypress="program-reward-option">
      <GradientRewardBox programColor={programColor}>
        <Box sx={{ position: "relative" }}>
          <PointsBadge amount={option.amount} />
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <RewardText title={option.label} description={option.description} />
          </Box>
        </Box>
      </GradientRewardBox>
    </Box>
  );
};

export const ProgramMediaCard = ({ config }: ProgramMediaCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const bgColor = config.programColor || "lightgrey";
  const bgColorDark = config.programColor || "lightgrey";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleCollapse = () => {
    setExpanded(false);
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Card
      ref={cardRef}
      data-cypress="program-card"
      sx={{
        width: "100%",
        backgroundColor: "background.paper",
        boxShadow: 2,
        borderRadius: 2,
        transition: "box-shadow 0.3s",
        "&:hover": {
          boxShadow: 4,
        },
        overflow: "clip",
      }}
    >
      <Box
        sx={{
          py: 2.5,
          px: 3,
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 10,
          borderBottom: "2px solid",
          borderBottomColor: "divider",
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            letterSpacing: "-0.5px",
          }}
        >
          {config.programName}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
        }}
      >
        <Box
          data-cypress="program-image-container"
          sx={{
            width: { xs: "100%", md: "400px" },
            height: { xs: "300px", md: "400px" },
            flexShrink: 0,
            flexGrow: 0,
            backgroundColor: "background.default",
            overflow: "hidden",
          }}
        >
          <FirebaseImage
            firebaseUrl={config.mediaUrl}
            alt={`${config.programName} program image`}
            width="100%"
            height="100%"
            objectFit="contain"
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            backgroundColor: bgColor,
            boxShadow: `inset 0 6px 5px -6px ${bgColorDark}, inset 0 -6px 5px -6px ${bgColorDark}`,
          }}
        >
          {config.rewardOptions.map((option) => (
            <ProgramRewardOption
              key={option.id}
              option={option}
              programColor={bgColor}
            />
          ))}
        </Box>
      </Box>

      {config.description && (
        <>
          <Box
            sx={{
              borderTop: "1px solid",
              borderTopColor: "divider",
              p: 1.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              data-cypress="program-details-toggle"
              onClick={() => setExpanded(!expanded)}
              variant="contained"
              fullWidth={isMobile}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
                />
              }
              sx={{
                fontWeight: 500,
                fontSize: "0.9rem",
                textTransform: "none",
              }}
            >
              {expanded ? "Hide Details" : "Show Details"}
            </Button>
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 3 },
                backgroundColor: "grey.50",
                color: "text.secondary",
              }}
            >
              <root.div>
                <MarkdownRenderer>{config.description}</MarkdownRenderer>
              </root.div>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  data-cypress="program-details-hide"
                  onClick={handleCollapse}
                  variant="contained"
                  fullWidth={isMobile}
                  endIcon={
                    <ExpandMoreIcon sx={{ transform: "rotate(180deg)" }} />
                  }
                  sx={{
                    mt: 2,
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    textTransform: "none",
                  }}
                >
                  Hide Details
                </Button>
              </Box>
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
};
